import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { boardsApi, type Board, type BoardGroupBy, type BoardInput, type BoardVisibility } from '@/api/boardsApi'
import { FormError } from '@/components/admin/AdminShared'
import { Field, NativeSelect } from '@/components/common/Field'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { BOARD_ICONS } from '@/components/board/BoardIcon'
import { useProducts } from '@/hooks/useDirectory'
import { PRIORITY_COLUMNS, STATUS_COLUMNS } from '@/lib/boardColumns'
import { BOARD_COLORS, BOARD_ICON_KEYS, BOARD_SWATCH, boardTint, isBoardColor, isBoardIcon, parseWipLimits } from '@/lib/boardLook'
import { statusLabel } from '@/lib/ticketLabels'
import { cn } from '@/lib/utils'

const GROUPS: BoardGroupBy[] = ['status', 'assignee', 'priority']

/** Create or edit a board: name and look, grouping, which tickets, column limits, and who can see it. */
export function BoardDialog({
  open,
  board,
  pending,
  error,
  onOpenChange,
  onSubmit,
}: {
  open: boolean
  board: Board | null
  pending: boolean
  error: unknown
  onOpenChange: (open: boolean) => void
  onSubmit: (input: BoardInput) => void
}) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{board ? t('boards.dialog.editTitle', { name: board.name }) : t('boards.dialog.createTitle')}</DialogTitle>
        </DialogHeader>
        {open ? <BoardForm key={board?.id ?? 'new'} board={board} pending={pending} error={error} onCancel={() => onOpenChange(false)} onSubmit={onSubmit} /> : null}
      </DialogContent>
    </Dialog>
  )
}

function Segmented<T extends string>({ value, options, label, onChange }: { value: T; options: { value: T; label: string }[]; label: string; onChange: (v: T) => void }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex w-fit rounded-md border border-line-strong p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-[5px] px-3 py-1.5 text-meta focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            value === o.value ? 'bg-accent font-medium text-accent-foreground' : 'text-ink-muted hover:text-ink',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function CheckList({ legend, options, value, onChange }: { legend: string; options: { id: string; label: string }[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <fieldset className="rounded-lg border border-line p-3">
      <legend className="px-1 text-eyebrow font-medium uppercase text-ink-muted">{legend}</legend>
      <ul className="flex max-h-36 flex-col gap-1.5 overflow-y-auto">
        {options.map((o) => (
          <li key={o.id}>
            <label className="flex items-center gap-2 text-meta text-ink-body">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={value.includes(o.id)}
                onChange={() => onChange(value.includes(o.id) ? value.filter((v) => v !== o.id) : [...value, o.id])}
              />
              {o.label}
            </label>
          </li>
        ))}
      </ul>
    </fieldset>
  )
}

function BoardForm({
  board,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  board: Board | null
  pending: boolean
  error: unknown
  onCancel: () => void
  onSubmit: (input: BoardInput) => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState(board?.name ?? '')
  const [groupBy, setGroupBy] = useState<BoardGroupBy>(board?.groupBy ?? 'status')
  const [products, setProducts] = useState<string[]>(board?.filters.product ?? [])
  const [priorities, setPriorities] = useState<string[]>(board?.filters.priority ?? [])
  const [assignee, setAssignee] = useState(board?.filters.assignee ?? '')
  const [text, setText] = useState(board?.filters.q ?? '')
  const [visibility, setVisibility] = useState<BoardVisibility>(board?.visibility ?? 'private')
  const [teamId, setTeamId] = useState(board?.team?.id ?? '')
  const [memberIds, setMemberIds] = useState<string[]>(board?.members.map((m) => m.id) ?? [])
  const [description, setDescription] = useState(board?.description ?? '')
  const [color, setColor] = useState<string | null>(isBoardColor(board?.color) ? board!.color : null)
  const [icon, setIcon] = useState<string | null>(isBoardIcon(board?.icon) ? board!.icon : null)
  const [wip, setWip] = useState<Record<string, string>>(() =>
    Object.fromEntries(Object.entries(board?.wipLimits ?? {}).map(([k, n]) => [k, String(n)])),
  )
  const wipParsed = parseWipLimits(wip)
  const [nameError, setNameError] = useState(false)
  const productList = useProducts()
  const targets = useQuery({ queryKey: ['boards', 'share-targets'], queryFn: boardsApi.shareTargets, staleTime: 60_000 })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setNameError(true)
      return
    }
    if (wipParsed.invalid.length) return
    const shared = visibility === 'shared'
    onSubmit({
      name: name.trim(),
      groupBy,
      description: description.trim() || null,
      color,
      icon,
      wipLimits: Object.keys(wipParsed.limits).length ? wipParsed.limits : null,
      filters: {
        ...(products.length ? { product: products } : {}),
        ...(priorities.length ? { priority: priorities } : {}),
        ...(assignee ? { assignee } : {}),
        ...(text.trim() ? { q: text.trim() } : {}),
      },
      visibility,
      teamId: shared && teamId ? teamId : null,
      memberIds: shared ? memberIds : [],
    })
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
      <Field id="b-name" label={t('boards.dialog.name')} error={nameError ? t('access.errors.required') : null}>
        <Input id="b-name" placeholder={t('boards.dialog.namePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <Field id="b-description" label={t('boards.dialog.description')}>
        <Textarea
          id="b-description"
          rows={2}
          maxLength={500}
          placeholder={t('boards.dialog.descriptionPlaceholder')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <span className="text-meta font-medium text-ink">{t('boards.dialog.color')}</span>
          <div role="radiogroup" aria-label={t('boards.dialog.color')} className="flex flex-wrap gap-1.5">
            <button
              type="button"
              role="radio"
              aria-checked={color === null}
              aria-label={t('boards.dialog.noColor')}
              title={t('boards.dialog.noColor')}
              onClick={() => setColor(null)}
              className={cn(
                'size-6 rounded-full border border-dashed border-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                color === null && 'ring-2 ring-primary ring-offset-2',
              )}
            />
            {BOARD_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={color === c}
                aria-label={t(`boards.colors.${c}`)}
                title={t(`boards.colors.${c}`)}
                onClick={() => setColor(c)}
                className={cn('size-6 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', BOARD_SWATCH[c], color === c && 'ring-2 ring-primary ring-offset-2')}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-meta font-medium text-ink">{t('boards.dialog.icon')}</span>
          <div role="radiogroup" aria-label={t('boards.dialog.icon')} className="flex flex-wrap gap-1">
            {BOARD_ICON_KEYS.map((k) => {
              const Icon = BOARD_ICONS[k]
              const on = (icon ?? 'kanban') === k
              return (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  aria-label={t(`boards.icons.${k}`)}
                  title={t(`boards.icons.${k}`)}
                  onClick={() => setIcon(k === 'kanban' ? null : k)}
                  className={cn(
                    'flex size-8 items-center justify-center rounded-md border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    on ? 'border-primary bg-accent' : 'border-line hover:border-line-strong',
                  )}
                >
                  <Icon className={cn('size-4', boardTint(color))} aria-hidden />
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-meta font-medium text-ink">{t('boards.dialog.groupBy')}</span>
        <Segmented label={t('boards.dialog.groupBy')} value={groupBy} onChange={setGroupBy} options={GROUPS.map((g) => ({ value: g, label: t(`boards.groups.${g}`) }))} />
      </div>

      <section className="flex flex-col gap-2" aria-label={t('boards.dialog.filters')}>
        <div>
          <p className="text-meta font-medium text-ink">{t('boards.dialog.filters')}</p>
          <p className="text-label text-ink-muted">{t('boards.dialog.filtersHelp')}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <CheckList legend={t('boards.dialog.products')} options={(productList.data ?? []).map((p) => ({ id: p.key, label: p.name }))} value={products} onChange={setProducts} />
          <CheckList legend={t('boards.dialog.priorities')} options={PRIORITY_COLUMNS.map((p) => ({ id: p, label: p }))} value={priorities} onChange={setPriorities} />
          <Field id="b-assignee" label={t('boards.dialog.assignee')}>
            <NativeSelect id="b-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">{t('boards.dialog.anyAssignee')}</option>
              {(targets.data?.agents ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field id="b-q" label={t('boards.dialog.search')}>
            <Input id="b-q" placeholder={t('boards.dialog.searchPlaceholder')} value={text} onChange={(e) => setText(e.target.value)} />
          </Field>
        </div>
      </section>

      <details className="group rounded-lg border border-line p-3" open={Object.keys(board?.wipLimits ?? {}).length > 0 || undefined}>
        <summary className="cursor-pointer text-meta font-medium text-ink marker:text-ink-muted">
          {t('boards.dialog.wip')}
          <span className="ml-2 font-normal text-ink-muted">{t('boards.dialog.wipHelp')}</span>
        </summary>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {STATUS_COLUMNS.filter((s) => s !== 'resolved').map((s) => {
            const bad = wipParsed.invalid.includes(s)
            return (
              <label key={s} className="flex items-center justify-between gap-3 text-meta text-ink-body">
                {statusLabel(s)}
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={999}
                  aria-invalid={bad || undefined}
                  placeholder="—"
                  value={wip[s] ?? ''}
                  onChange={(e) => setWip({ ...wip, [s]: e.target.value })}
                  className={cn('h-8 w-20 text-right', bad && 'border-destructive')}
                />
              </label>
            )
          })}
        </div>
        {wipParsed.invalid.length ? <p role="alert" className="mt-2 text-label text-destructive">{t('boards.dialog.wipInvalid')}</p> : null}
      </details>

      <section className="flex flex-col gap-2" aria-label={t('boards.dialog.sharing')}>
        <span className="text-meta font-medium text-ink">{t('boards.dialog.sharing')}</span>
        <Segmented
          label={t('boards.dialog.sharing')}
          value={visibility}
          onChange={setVisibility}
          options={[
            { value: 'private', label: t('boards.dialog.private') },
            { value: 'shared', label: t('boards.dialog.shared') },
          ]}
        />
        {visibility === 'shared' ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="b-team" label={t('boards.dialog.team')}>
              <NativeSelect id="b-team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                <option value="">{t('boards.dialog.noTeam')}</option>
                {(targets.data?.teams ?? []).map((tm) => (
                  <option key={tm.id} value={tm.id}>
                    {tm.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <CheckList legend={t('boards.dialog.members')} options={(targets.data?.agents ?? []).map((a) => ({ id: a.id, label: a.name }))} value={memberIds} onChange={setMemberIds} />
          </div>
        ) : null}
      </section>

      <FormError error={error} />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={pending}>
          {board ? t('boards.dialog.save') : t('boards.dialog.create')}
        </Button>
      </div>
    </form>
  )
}
